/**
 * RAW rescue — UI layer.
 *
 * The engine returns previews as either a ready JPEG or as raw RGB pixels; the
 * second kind is painted onto a canvas here, because that is the only place a
 * browser can turn pixels back into a file.
 */

import { rescue, RawError } from './raw-preview.js';

const T = (k, p) => {
  let s = (window.__RR_I18N && window.__RR_I18N[k]) || k;
  if (p) for (const a of Object.keys(p).sort((x, y) => y.length - x.length)) {
    s = s.split(':' + a).join(p[a]);
  }
  return s;
};
const el = (id) => document.getElementById(id);
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

function human(bytes) {
  const u = [T('unit_b'), T('unit_kb'), T('unit_mb'), T('unit_gb')];
  let n = Number(bytes) || 0, i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n < 10 && i ? n.toFixed(1) : Math.round(n)} ${u[i]}`;
}

const state = { file: null, result: null, urls: [] };

function show(html) { const o = el('rr-out'); o.innerHTML = html; o.hidden = false; }
const card = (tone, title, body, extra = '') =>
  `<div class="rr-card rr-tone-${tone}"><p class="rr-title">${title}</p>
     <p class="rr-body">${body}</p>${extra}</div>`;

/**
 * A preview as something the page can show and the person can save.
 *
 * A JPEG preview is already a file. RGB pixels are not — they are turned into
 * one through a canvas, which is also the only way to hand them to an <img>.
 */
async function toBlob(p) {
  if (p.kind === 'jpeg') return new Blob([p.bytes], { type: 'image/jpeg' });

  const { w, h } = p.size;
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d');
  const img = ctx.createImageData(w, h);
  // RGB triplets in, RGBA out — the alpha byte is ours to add.
  for (let i = 0, j = 0; i < w * h; i++, j += 3) {
    img.data[i * 4] = p.bytes[j];
    img.data[i * 4 + 1] = p.bytes[j + 1];
    img.data[i * 4 + 2] = p.bytes[j + 2];
    img.data[i * 4 + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.95));
}

const baseName = (file) => String(file.name || 'photo').replace(/\.[^.]+$/, '') || 'photo';

async function onFile(file) {
  state.file = file;
  for (const u of state.urls) URL.revokeObjectURL(u);
  state.urls = [];
  show(`<div class="rr-working">${T('reading')}</div>`);
  window.__stage && window.__stage('reading');

  let res;
  try {
    res = await rescue(new Uint8Array(await file.arrayBuffer()));
  } catch (err) {
    const code = err instanceof RawError ? err.code : 'error';
    show(card('bad', T('verdict_none'), T(`body_${code}`) === `body_${code}` ? T('body_none') : T(`body_${code}`)));
    window.__aeDiagnostic && window.__aeDiagnostic({
      issue: code, ext: (file.name || '').split('.').pop().toLowerCase(), size: file.size,
      // non-RawError crashes reported a bare "error" — 7 in 30 days with
      // nothing to debug from. The message is the difference between a
      // fixable signal and a rumour.
      m: err instanceof RawError ? undefined : String(err && err.message || err).slice(0, 140) });
    return;
  }
  state.result = res;
  if (!res.best) { renderSensorOnly(res, file); return; }
  await render(res, file);
}

/**
 * No viewable preview survived, but the sensor stream did. That is an answer,
 * not a failure: the person learns the photograph is alive inside the file
 * and what class of software can develop it. Nothing is billed — this is a
 * diagnosis, and diagnoses are free by policy.
 */
function renderSensorOnly(res, file) {
  const s = res.sensor[0];
  const facts = [
    res.facts.make || res.facts.model ? [T('fact_camera'), `${res.facts.make || ''} ${res.facts.model || ''}`.trim()] : null,
    res.facts.taken ? [T('fact_taken'), res.facts.taken] : null,
    [T('fact_sensor_stream'), `${s.size.w}×${s.size.h} · ${human(s.length)}`],
    [T('fact_size'), human(file.size)],
  ].filter(Boolean).map(([k, v]) => `<tr><th scope="row">${k}</th><td>${esc(v)}</td></tr>`).join('');

  show(card('warn', T('verdict_sensor_only'), T('body_sensor_only'),
      `<div class="rr-actions">
         <button type="button" class="rr-btn" id="rr-develop">${T('develop_btn')}</button>
       </div>
       <p class="rr-body rr-dim">${T('develop_hint')}</p>
       <div id="rr-develop-out"></div>`)
    + `<section class="rr-block"><h3>${T('facts_title')}</h3><table class="rr-facts">${facts}</table></section>`);

  const btn = el('rr-develop');
  if (btn) btn.addEventListener('click', () => runDevelop(res, file, false));

  window.__stage && window.__stage('analyzed');
  window.__aeToolComplete && window.__aeToolComplete({
    flavour: res.flavour, previews: 0, usable: 0, sensorOnly: 1,
    sensorW: s.size.w, sensorH: s.size.h,
    sizeMB: Math.round(file.size / 1048576),
  });
}

/**
 * Develop the photograph from the surviving sensor stream, off the main
 * thread. The decode of a ~25 MB stream runs 10-15 s — a progress line is not
 * decoration here. The R↔B swap re-develops with the channels exchanged: the
 * automatic choice follows the warm-illuminant prior, which the rare
 * blue-hour scene genuinely defeats — one honest toggle beats a wrong guess.
 */
let devWorker = null;
async function runDevelop(res, file, swapRB) {
  const out = el('rr-develop-out');
  const btn = el('rr-develop');
  if (!out || !window.__RR_DEVELOP_URL) return;
  if (btn) btn.disabled = true;
  out.innerHTML = `<div class="rr-working" id="rr-dev-prog">${T('developing')}</div>`;
  window.__stage && window.__stage('developing');

  try {
    if (!devWorker) devWorker = new Worker(window.__RR_DEVELOP_URL);
    const payload = { cmd: 'develop', at: res.sensor[0].offset, swapRB };
    if (!devWorker.__hasBytes) {
      const buf = await file.arrayBuffer();
      payload.bytes = buf;
    }
    const result = await new Promise((resolve, reject) => {
      devWorker.onmessage = (ev) => {
        const m = ev.data || {};
        if (m.type === 'progress') {
          const p = el('rr-dev-prog');
          if (p) p.textContent = `${T('developing')} ${Math.round((m.phase === 'decode' ? m.f * 0.8 : 0.8 + m.f * 0.2) * 100)}%`;
        } else if (m.type === 'result') resolve(m);
        else if (m.type === 'error') reject(new Error(m.message));
      };
      devWorker.onerror = (e) => reject(new Error(e.message || 'worker error'));
      devWorker.postMessage(payload, payload.bytes ? [payload.bytes] : []);
    });
    devWorker.__hasBytes = true;

    const canvas = document.createElement('canvas');
    canvas.width = result.w; canvas.height = result.h;
    canvas.getContext('2d').putImageData(new ImageData(new Uint8ClampedArray(result.rgba), result.w, result.h), 0, 0);
    const blob = await new Promise((r) => canvas.toBlob(r, 'image/jpeg', 0.92));
    const url = URL.createObjectURL(blob);
    state.urls.push(url);
    out.innerHTML = `<div class="rr-shot"><img src="${url}" alt="${T('alt_preview')}" decoding="async"></div>
      <div class="rr-actions">
        <button type="button" class="rr-btn" id="rr-dev-save">${T('save_jpeg')}</button>
        <button type="button" class="rr-mini" id="rr-dev-swap">${T('develop_swap')}</button>
      </div>`;
    el('rr-dev-save').addEventListener('click', async () => {
      let run = { commit() {}, abort() {} };
      if (window.__toolLimit) {
        const key = window.__toolLimit.fileKey ? window.__toolLimit.fileKey(file) : null;
        run = window.__toolLimit.begin('raw-rescue', key) || run;
      }
      try {
        window.__downloadFile(blob, `${baseName(file)}-developed.jpg`);
        run.commit();
      } catch (err) { run.abort(); window.__handleError && window.__handleError(err, 'raw-rescue/develop-save'); }
    });
    el('rr-dev-swap').addEventListener('click', () => runDevelop(res, file, !swapRB));

    window.__stage && window.__stage('developed');
    window.__aeToolComplete && window.__aeToolComplete({
      flavour: res.flavour, developed: 1, devW: result.w, devH: result.h,
      swapRB: swapRB ? 1 : 0, slices: (result.meta.slices || []).length,
      sizeMB: Math.round(file.size / 1048576),
    });
  } catch (err) {
    out.innerHTML = `<p class="rr-body">${T('develop_failed')}</p>`;
    window.__aeToolError && window.__aeToolError({ errorType: 'develop_failed', message: String(err && err.message || err).slice(0, 120) });
  } finally {
    if (btn) btn.disabled = false;
  }
}

async function render(res, file) {
  const good = res.previews.filter((p) => p.ok);
  const best = res.best;
  const blob = await toBlob(best);
  const url = URL.createObjectURL(blob);
  state.urls.push(url);

  const facts = [
    res.facts.make || res.facts.model ? [T('fact_camera'), `${res.facts.make || ''} ${res.facts.model || ''}`.trim()] : null,
    res.facts.lens ? [T('fact_lens'), res.facts.lens] : null,
    res.facts.taken ? [T('fact_taken'), res.facts.taken] : null,
    [T('fact_format'), res.flavour.toUpperCase()],
    [T('fact_previews'), String(res.previews.length)],
    [T('fact_size'), human(file.size)],
  ].filter(Boolean).map(([k, v]) => `<tr><th scope="row">${k}</th><td>${esc(v)}</td></tr>`).join('');

  /* What was actually wrong with the file, said only when it was. */
  const notes = [];
  if (res.viaSignature) notes.push(T('note_via_signature'));
  if (res.headerError) notes.push(T('note_header_broken'));
  if (best.kind === 'rgb') notes.push(T('note_rgb_preview'));
  if (!best.ok) notes.push(T('note_preview_damaged'));
  const notesHtml = notes.length
    ? `<section class="rr-block"><h3>${T('notes_title')}</h3>
         <ul class="rr-notes">${notes.map((n) => `<li>${n}</li>`).join('')}</ul></section>` : '';

  const others = res.previews.length > 1
    ? `<section class="rr-block"><h3>${T('others_title', { n: String(res.previews.length - 1) })}</h3>
         <p class="rr-body">${T('others_desc')}</p>
         <ul class="rr-list">${res.previews.slice(1).map((p, i) =>
           `<li>${p.size ? `${p.size.w}×${p.size.h}` : '—'} · ${human(p.length)} · ${T(`via_${p.via}`)}
              <button type="button" class="rr-mini" data-save="${i + 1}">${T('save_this')}</button></li>`).join('')}</ul>
       </section>` : '';

  show(card(best.ok ? 'ok' : 'warn',
      T('verdict_found', { w: String(best.size ? best.size.w : '?'), h: String(best.size ? best.size.h : '?') }),
      T(res.viaSignature ? 'body_found_signature' : 'body_found'),
      /* NOT loading="lazy". This image is the whole result — on a phone it sits
       * below the fold, so lazy loading means the person scrolls down to an
       * empty box and waits for the thing they already waited for. Measured on
       * WebKit at 390 px: naturalWidth stayed 0 until the image was scrolled
       * into view. Lazy is for decoration, and this is the answer. */
      `<div class="rr-shot"><img src="${url}" alt="${T('alt_preview')}" decoding="async"></div>
       <div class="rr-actions">
         <button type="button" class="rr-btn" id="rr-save">${T('save_jpeg')}</button>
       </div>`)
    + `<section class="rr-block"><h3>${T('facts_title')}</h3><table class="rr-facts">${facts}</table></section>`
    + notesHtml + others);

  wire(res, file);

  window.__stage && window.__stage('analyzed');
  window.__aeToolComplete && window.__aeToolComplete({
    flavour: res.flavour, previews: res.previews.length, usable: good.length,
    bestW: best.size ? best.size.w : 0, bestH: best.size ? best.size.h : 0,
    kind: best.kind, via: best.via, viaSignature: res.viaSignature ? 1 : 0,
    sizeMB: Math.round(file.size / 1048576),
  });
}

function wire(res, file) {
  /* The unit is the FILE. Saving the big preview and then a smaller one is one
   * job, not two. */
  let charged = false;
  const bill = () => {
    if (charged || !window.__toolLimit) { charged = true; return { commit() {}, abort() {} }; }
    const key = window.__toolLimit.fileKey ? window.__toolLimit.fileKey(file) : null;
    return window.__toolLimit.begin('raw-rescue', key);
  };

  const save = async (p, suffix) => {
    const run = bill();
    if (!run) return;
    try {
      const blob = await toBlob(p);
      window.__downloadFile(blob, `${baseName(file)}${suffix}.jpg`);
      run.commit();
    } catch (err) {
      run.abort();
      window.__aeToolError && window.__aeToolError({ errorType: 'encode_failed', message: String(err).slice(0, 120) });
      window.__handleError && window.__handleError(err, 'raw-rescue/save');
    }
  };

  const b = el('rr-save');
  if (b) b.addEventListener('click', () => save(res.best, ''));
  document.querySelectorAll('[data-save]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const p = res.previews[Number(btn.getAttribute('data-save'))];
      if (p) save(p, `-${p.size ? p.size.w : 'alt'}`);
    });
  });
}

document.addEventListener('DOMContentLoaded', () => {
  const zone = el('rrDrop');
  if (!zone) return;
  zone.addEventListener('tool-uploader:file', (ev) => {
    const f = ev.detail && (ev.detail.file || (ev.detail.files && ev.detail.files[0]));
    if (f) onFile(f);
  });
});

export { onFile };
